import React from "react";
import {
  Box,
  Typography,
  Button,
  Card,
  Stack,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import FunctionsIcon from "@mui/icons-material/Functions";
import TimerIcon from "@mui/icons-material/Timer";
import CalculateIcon from "@mui/icons-material/Calculate";
import { useNavigate, useParams } from "react-router-dom";

import LandingBackground from "../components/Landing/LandingBackground";
import useAuth from "../hooks/useAuth";
import useTestSession from "../hooks/useTestSession";
import { markAssignmentInProgress } from "../utils/assignments/markAssignmentInProgress";

const TestLandingPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { testId } = useParams();

  const { sessionType, definition, progress, questions, loading } =
    useTestSession(testId, user?.uid);

  if (loading || !definition || !progress) return null;

  /* ===============================
     DERIVED DISPLAY DATA
  =============================== */

  const testType = "SAT"; // fine to hardcode for now

  const category =
    sessionType === "diagnostic"
      ? definition.section
      : progress.section ?? definition.section ?? "";

  const title =
    sessionType === "diagnostic"
      ? definition.name
      : progress.packetName ?? definition.name ?? "";

  const numQuestions =
    sessionType === "diagnostic"
      ? definition.administeredCount
      : definition.questionIds?.length ?? "--";

  const estimatedMinutes =
    sessionType === "diagnostic"
      ? definition.estimatedMinutes ?? "--"
      : progress.estimatedMinutes ?? "--";

  const calculatorAllowed =
    sessionType === "diagnostic"
      ? false
      : progress.calculatorAllowed ?? false;

  const status = progress.status;

  const startButtonLabel =
    status === "completed" || status === "review"
      ? "Review Test"
      : status === "in_progress"
      ? "Resume Test"
      : "Start Test";

  /* ===============================
     HANDLERS
  =============================== */

  const handleBack = () => {
    navigate(-1);
  };

  const handleStart = async () => {
    if (!user || !testId) return;

    const path =
      sessionType === "diagnostic"
        ? `/diagnostic/${testId}`
        : `/test/${testId}/question`;

    // Only assignments need marking here
    if (sessionType === "assignment") {
      if (status !== "completed" && status !== "review") {
        await markAssignmentInProgress({
          studentUid: user.uid,
          packetId: testId,
          currentStatus: status,
        });
      }
    }

    navigate(path, {
      state: {
        sessionType,
        definition,
        progress,
        questions,
      },
    });
  };

  /* ===============================
     RENDER
  =============================== */

  return (
    <Box
      sx={{
        minHeight: "100vh",
        position: "relative",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <LandingBackground />

      {/* Header */}
      <Box
        sx={{
          position: "relative",
          zIndex: 1,
          p: 3,
          display: "flex",
          alignItems: "center",
          gap: 1,
        }}
      >
        <Button
          onClick={handleBack}
          startIcon={<ArrowBackIcon />}
          variant="text"
          sx={{
            textTransform: "none",
            fontSize: 16,
            px: 3,
            py: 1,
            borderRadius: 10,
            color: "text.primary",
            justifyContent: "flex-start",
            "&:hover": {
              backgroundColor: "rgba(0,0,0,0.06)",
            },
          }}
        >
          Return to Dashboard
        </Button>
      </Box>

      {/* Main Card */}
      <Box
        sx={{
          position: "relative",
          zIndex: 1,
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          px: 2,
          transform: "translateY(-48px)",
        }}
      >
        <Card
          sx={{
            width: "100%",
            maxWidth: 720,
            p: 6,
            borderRadius: 4,
            backgroundColor: "rgba(255,255,255,0.96)",
            boxShadow: "0 12px 32px rgba(0,0,0,0.15)",
            textAlign: "center",
          }}
        >
          <Typography
            variant="h6"
            color="text.secondary"
            fontWeight={600}
            gutterBottom
          >
            {testType} • {category}
          </Typography>

          <Typography
            variant="h2"
            fontWeight={700}
            sx={{ mt: 1, mb: 4 }}
          >
            {title}
          </Typography>

          <Stack spacing={3} sx={{ mb: 4 }}>
            <Stack direction="row" spacing={2} justifyContent="center">
              <FunctionsIcon fontSize="large" />
              <Typography variant="h5" fontWeight={600}>
                {numQuestions} Questions
              </Typography>
            </Stack>

            <Stack direction="row" spacing={2} justifyContent="center">
              <TimerIcon fontSize="large" />
              <Typography variant="h5" fontWeight={600}>
                {estimatedMinutes} Minutes
              </Typography>
            </Stack>
          </Stack>

          {/* Diagnostic Blurb */}
          {sessionType === "diagnostic" && (
            <Box
              sx={{
                mb: 5,
                px: 2,
              }}
            >
              <Typography
                variant="body1"
                color="text.secondary"
                sx={{ lineHeight: 1.2 }}
              >
                This is an <strong>adaptive diagnostic test</strong>. You'll see
                questions one at a time, and you won't be able to go back to
                previous questions.
                <br />
                <br />
                The time shown is a pacing guide, not a hard limit. If the timer
                reaches zero, you can and should continue working until you
                finish.
              </Typography>
            </Box>
          )}

          <Button
            variant="contained"
            size="large"
            onClick={handleStart}
            sx={{
              px: 7,
              py: 2,
              borderRadius: 2,
              fontSize: 18,
              fontWeight: 600,
            }}
          >
            {startButtonLabel}
          </Button>
        </Card>
      </Box>
    </Box>
  );
};

export default TestLandingPage;
